import React, { useState } from 'react';
import { history, useIntl, useDispatch } from 'umi';
import { Layout } from 'tea-component';
import Tabs from '@intelligence/components/NewIconTabs';
import ActiveUsers from '@intelligence/v3/pages/TopCharts/MobileGames/ActiveUsers';
import Revenue from '@intelligence/v3/pages/TopCharts/MobileGames/Revenue';
import Downloads from '@intelligence/v3/pages/TopCharts/MobileGames/Downloads';
import RPD from '@intelligence/pages/TopCharts/MobileGames/RPD';
import Retention from '@intelligence/pages/TopCharts/MobileGames/Retention';
import Demographics from '@intelligence/pages/TopCharts/MobileGames/Demographics';
import NewRelease from '@intelligence/pages/TopCharts/MobileGames/NewRelease';
import Upcoming from '@intelligence/pages/TopCharts/MobileGames/Upcoming';
import DataBrainCalibration from '@intelligence/v3/pages/TopCharts/MobileGames/DataBrainCalibration';
import PermissionGuard from '@intelligence/v3/pages/TopCharts/MobileGames/_components/PermissionGuard';
import { dbiPageLogMes } from '@intelligence/utils/buriedPointCfg';
// import DataBrainCalibration from '@intelligence/pages/TopCharts/MobileGames/DataBrainCalibration';
import { DBI_TOPCHARTS } from '@intelligence/models/topcharts';
import { TopchartPageId } from '@intelligence/pages/TopCharts/_config/topChartsConst';

import type { Tab } from 'tea-component';

import '@intelligence/assets/scss/TopCharts/MobileGames/index.scss';

import ICON_DATASOURCE_OGDB from '@intelligence/assets/svg/dataSource/ogdb.svg';

const { Content } = Layout;

/**
 * tab页面的key常量
 */
const TabKeyMapping = {
  ActiveUsers: 'ActiveUsers',
  Revenue: 'Revenue',
  Downloads: 'Downloads',
  RPD: 'RPD',
  Retention: 'Retention',
  Demographics: 'Demographics',
  NewRelease: 'NewRelease',
  Upcoming: 'Upcoming',
  DataBrainCalibration: 'DataBrainCalibration',
};

/**
 * 游戏需要继承的榜单列表
 */
const GAMES_EXTENDABLE_TOP_CHARTS = [TabKeyMapping.ActiveUsers, TabKeyMapping.Revenue, TabKeyMapping.Downloads];

const PERMISSION_GUARD_STYLE: React.CSSProperties = {
  height: 'calc(100vh - 60px - 54px - 40px)',
};

const withPermissionGuard = (pageId: number | string, children: React.ReactNode) => (
  <PermissionGuard pageId={pageId} style={PERMISSION_GUARD_STYLE}>
    {children}
  </PermissionGuard>
);

/**
 * Body的loading状态
 */
function useBodyLoading() {
  const [bodyLoading, setBodyLoading] = useState(false);

  const executeSimulateBodyLoading = () => {
    setBodyLoading(true);

    setTimeout(() => {
      setBodyLoading(false);
    }, 0);
  };

  return { bodyLoading, executeSimulateBodyLoading };
}

const MobileGames: React.FC<any> = (props) => {
  const {} = props;

  const { formatMessage } = useIntl();

  const dispatch = useDispatch();

  const { bodyLoading, executeSimulateBodyLoading } = useBodyLoading();

  const [activeId, setActiveId] = useState(props?.match?.params?.tab || TabKeyMapping.ActiveUsers);

  const tabs = [
    {
      id: TabKeyMapping.ActiveUsers,
      label: formatMessage({ id: `dbi_active_users` }),
      component: withPermissionGuard(
        dbiPageLogMes.topchartsMobileGamesActiveUsers.pageId,
        <ActiveUsers pageId={TopchartPageId.MobileGames.ActiveUsers} />,
      ),
    },
    {
      id: TabKeyMapping.Revenue,
      label: formatMessage({ id: `dbi_revenue` }),
      component: withPermissionGuard(
        dbiPageLogMes.topchartsMobileGamesRevenue.pageId,
        <Revenue pageId={TopchartPageId.MobileGames.Revenue} />,
      ),
    },
    {
      id: TabKeyMapping.Downloads,
      label: formatMessage({ id: `dbi_downloads` }),
      component: withPermissionGuard(
        dbiPageLogMes.topchartsMobileGamesDownloads.pageId,
        <Downloads pageId={TopchartPageId.MobileGames.Downloads} />,
      ),
    },
    {
      id: TabKeyMapping.RPD,
      label: formatMessage({ id: `dbi_rpd` }),
      component: withPermissionGuard(
        dbiPageLogMes.topchartsMobileGamesRPD.pageId,
        <RPD pageId={TopchartPageId.MobileGames.RPD} />,
      ),
    },
    {
      id: TabKeyMapping.Retention,
      label: formatMessage({ id: `dbi_retention` }),
      component: withPermissionGuard(
        dbiPageLogMes.topChartsMobileGamesRetention.pageId,
        <Retention pageId={TopchartPageId.MobileGames.Retention} />,
      ),
    },
    {
      id: TabKeyMapping.Demographics,
      label: formatMessage({ id: `dbi_demographics` }),
      component: withPermissionGuard(
        dbiPageLogMes.topchartMobileGamesDemographics.pageId,
        <Demographics pageId={TopchartPageId.MobileGames.Demographics} />,
      ),
    },
    {
      id: TabKeyMapping.NewRelease,
      label: formatMessage({ id: `dbi_new_release` }),
      component: <NewRelease pageId={TopchartPageId.MobileGames.NewRelease} />,
    },
    {
      id: TabKeyMapping.Upcoming,
      label: formatMessage({ id: `dbi_upcoming` }),
      component: <Upcoming pageId={TopchartPageId.MobileGames.Upcoming} />,
    },
    {
      id: TabKeyMapping.DataBrainCalibration,
      label: (
        <div className="dbi-tab-title">
          <img className="dbi-tab-title-icon-new" src={ICON_DATASOURCE_OGDB} alt="" />
          <span className="dbi-tab-title-text">{formatMessage({ id: `dbi_dataBrain_calibration` })}</span>
        </div>
      ),
      component: withPermissionGuard(
        dbiPageLogMes.topchartsMobileGamesDataBrainCalibration.pageId,
        <DataBrainCalibration pageId={TopchartPageId.MobileGames.DataBrain} />,
      ),
    },
  ];

  const activeTab = tabs.find((tab) => tab.id === activeId);

  const handleActive = (tab: Tab) => {
    const tabId = tab.id;

    // 不是游戏继承榜单则清除继承
    if (!GAMES_EXTENDABLE_TOP_CHARTS.includes(tabId)) {
      dispatch({ type: `${DBI_TOPCHARTS}/setActiveUserGameList`, payload: [] });
    }

    history.replace(`/intelligence/topCharts/mobileGames/${tabId}`);

    setActiveId(tabId);

    executeSimulateBodyLoading();
  };

  return (
    <Content className="affix-target dbi-topcharts-list">
      <Content.Header title={formatMessage({ id: `dbi_mobile_games` })} className="contentHead-normal">
        <div className="header-tab">
          <Tabs
            ceiling
            newMarkIdsMappingKey="TopChartsMobileGames"
            animated={false}
            tabs={tabs}
            activeId={activeId}
            onActive={handleActive}
          />
        </div>
      </Content.Header>
      <Content.Body full>{!bodyLoading && <div id="title">{activeTab?.component}</div>}</Content.Body>
    </Content>
  );
};

export default MobileGames;
